import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import AuthLayout from "../components/AuthLayout";
import { Input, Label } from "../components/ui/Input";
import { Button } from "../components/ui/Button";
import {
  Warning,
  CheckCircle,
  CaretRight,
  Plus,
  Minus,
  Check,
} from "@phosphor-icons/react";

const SIZE_OPTIONS = [
  {
    value: "starter",
    title: "MINI",
    bullets: [
      "Für Unternehmen bis 50 Mitarbeiter",
      "Für Steuerberater, Rechtsanwälte, BiBuG-Selbständige bis 10 Mitarbeiter",
    ],
  },
  {
    value: "growth",
    title: "MIDI",
    bullets: [
      "Für Unternehmen bis 250 Mitarbeiter",
      "Für Steuerberater, Rechtsanwälte, BiBuG-Selbständige bis 50 Mitarbeiter",
    ],
  },
  {
    value: "enterprise",
    title: "MAXI",
    bullets: [
      "Für Unternehmen über 250 Mitarbeiter",
      "Für Steuerberater, Rechtsanwälte, BiBuG-Selbständige über 50 Mitarbeiter",
    ],
  },
];

function SectionTitle({ index, title, hint }) {
  return (
    <div className="border-b border-ink-200 pb-3 mb-5">
      <div className="flex items-baseline gap-2">
        <span className="font-cabinet text-[11px] uppercase tracking-widest font-bold text-brand-500">
          {String(index).padStart(2, "0")}
        </span>
        <h3 className="font-cabinet text-base font-semibold text-ink-900 tracking-tight">
          {title}
        </h3>
      </div>
      {hint && <p className="mt-1 text-[11px] text-warm-500">{hint}</p>}
    </div>
  );
}

function SizeTile({ option, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(option.value)}
      data-testid={`register-size-${option.value}`}
      aria-pressed={selected}
      className={[
        "group text-left border rounded-sm p-5 transition-crisp focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 flex flex-col h-full overflow-hidden",
        selected
          ? "border-brand-400 bg-brand-50 shadow-[0_4px_24px_-12px_rgba(245,166,35,0.45)]"
          : "border-ink-200 bg-white hover:border-ink-400 hover:shadow-[0_4px_24px_-16px_rgba(15,23,42,0.20)]",
      ].join(" ")}
    >
      <div className="flex items-center justify-between mb-4">
        <span className="font-cabinet text-2xl font-black tracking-tight text-ink-900">
          {option.title}
          <span className="text-brand-400">.</span>
        </span>
        {selected && (
          <CheckCircle size={18} weight="fill" className="text-brand-500" />
        )}
      </div>
      <ul className="space-y-3 flex-1">
        {option.bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2">
            <CaretRight
              size={14}
              weight="fill"
              className="text-brand-500 mt-1 shrink-0"
            />
            <span
              className="text-[12.5px] text-ink-900 leading-snug font-medium hyphens-auto"
              style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
              lang="de"
            >
              {b}
            </span>
          </li>
        ))}
      </ul>
    </button>
  );
}

function CheckboxRow({ id, checked, onChange, children, testId }) {
  return (
    <label
      htmlFor={id}
      className="flex items-start gap-3 cursor-pointer group"
      data-testid={testId}
    >
      <span
        aria-hidden="true"
        className={[
          "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border transition-crisp",
          checked
            ? "border-brand-500 bg-brand-400 text-ink-900"
            : "border-ink-300 bg-white group-hover:border-ink-500",
        ].join(" ")}
      >
        {checked && <Check size={14} weight="bold" />}
      </span>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
      <span className="text-sm text-ink-900 leading-snug">{children}</span>
    </label>
  );
}

const initialForm = {
  first_name: "",
  last_name: "",
  email: "",
  password: "",
  company: "",
  company_size: "",
  address_street: "",
  address_postal_code: "",
  address_city: "",
  billing_address_different: false,
  billing_email: "",
};

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [agreeDsgvo, setAgreeDsgvo] = useState(false);
  const [agreeAgb, setAgreeAgb] = useState(false);

  const set = (name, value) => setForm((f) => ({ ...f, [name]: value }));
  const handleChange = (e) => set(e.target.name, e.target.value);

  const sizeSelected = !!form.company_size;
  const canSubmit =
    sizeSelected && agreeDsgvo && agreeAgb && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.company_size) {
      setError("Bitte wählen Sie zuerst eine Unternehmensgröße aus.");
      return;
    }
    if (form.password.length < 8) {
      setError("Das Passwort muss mindestens 8 Zeichen lang sein.");
      return;
    }
    if (
      form.billing_address_different &&
      !form.billing_email.trim()
    ) {
      setError(
        "Bei abweichender Rechnungs-E-Mail muss eine Adresse angegeben sein."
      );
      return;
    }
    if (!agreeDsgvo || !agreeAgb) {
      setError("Bitte bestätigen Sie Datenschutz und AGB, um fortzufahren.");
      return;
    }
    setSubmitting(true);
    const payload = {
      email: form.email.trim().toLowerCase(),
      password: form.password,
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      company: form.company.trim(),
      company_size: form.company_size,
      address_street: form.address_street.trim(),
      address_postal_code: form.address_postal_code.trim(),
      address_city: form.address_city.trim(),
      billing_address_different: form.billing_address_different,
      ...(form.billing_address_different && {
        billing_email: form.billing_email.trim().toLowerCase(),
      }),
    };
    const result = await register(payload);
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    navigate("/pending", { replace: true });
  };

  return (
    <AuthLayout
      title="Registrieren"
      subtitle=""
      wide
      footer={
        <>
          Bereits ein Konto?{" "}
          <Link
            to="/login"
            data-testid="register-link-login"
            className="font-medium text-ink-900 underline underline-offset-4 hover:no-underline"
          >
            LOGIN
          </Link>
        </>
      }
    >
      <form
        onSubmit={handleSubmit}
        className="space-y-8"
        data-testid="register-form"
        noValidate
      >
        {/* Intro / explanation */}
        <ol className="space-y-3 text-sm text-warm-700 leading-relaxed">
          {[
            "Klicken Sie unterhalb auf Ihre Unternehmensgröße.",
            "Nach Eingabe Ihrer Daten und Absenden Ihrer Bestellung erfolgt die Freischaltung manuell durch unser Team. Sie werden per E-Mail über die Freischaltung informiert.",
            "Die Rechnung folgt anschließend per E-Mail.",
          ].map((text, idx) => (
            <li key={idx} className="relative pl-10">
              <span className="absolute left-0 top-0.5 font-cabinet text-[11px] uppercase tracking-widest font-bold text-brand-500">
                {String(idx + 1).padStart(2, "0")}
              </span>
              <span>{text}</span>
            </li>
          ))}
        </ol>

        {/* SECTION 01 — COMPANY SIZE (always shown first) */}
        <section>
          <SectionTitle
            index={1}
            title="Unternehmensgröße"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {SIZE_OPTIONS.map((opt) => (
              <SizeTile
                key={opt.value}
                option={opt}
                selected={form.company_size === opt.value}
                onSelect={(v) => set("company_size", v)}
              />
            ))}
          </div>
        </section>

        {/* Remaining sections, gated on size selection */}
        {sizeSelected && (
          <>
            {/* SECTION 02 — UNTERNEHMEN (Firmenname zuerst, dann persönliche Daten als Firmenkontakt) */}
            <section data-testid="register-section-company">
              <SectionTitle index={2} title="Unternehmen" />
              <div className="space-y-4">
                <div>
                  <Label htmlFor="company">Firmenname</Label>
                  <Input
                    id="company"
                    name="company"
                    type="text"
                    required
                    autoComplete="organization"
                    value={form.company}
                    onChange={handleChange}
                    placeholder="Mustermann GmbH"
                    data-testid="register-company-input"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">Vorname (Ansprechperson)</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      type="text"
                      required
                      autoComplete="given-name"
                      value={form.first_name}
                      onChange={handleChange}
                      placeholder="Max"
                      data-testid="register-first-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Nachname (Ansprechperson)</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      type="text"
                      required
                      autoComplete="family-name"
                      value={form.last_name}
                      onChange={handleChange}
                      placeholder="Mustermann"
                      data-testid="register-last-name-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email">E-Mail-Adresse</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="ihre.adresse@firma.at"
                    data-testid="register-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Passwort</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    required
                    minLength={8}
                    autoComplete="new-password"
                    value={form.password}
                    onChange={handleChange}
                    placeholder="Mindestens 8 Zeichen"
                    data-testid="register-password-input"
                  />
                  <p className="mt-1.5 text-[11px] text-warm-500">
                    Mindestens 8 Zeichen. Empfohlen: Sonderzeichen und Zahlen.
                  </p>
                </div>
              </div>
            </section>

            {/* SECTION 03 — ADRESSE (inkl. abweichende Rechnungsadresse) */}
            <section>
              <SectionTitle
                index={3}
                title="Adresse"
                hint="Hauptanschrift Ihres Unternehmens."
              />
              <div className="space-y-4">
                <div>
                  <Label htmlFor="address_street">Straße &amp; Hausnummer</Label>
                  <Input
                    id="address_street"
                    name="address_street"
                    type="text"
                    required
                    autoComplete="street-address"
                    value={form.address_street}
                    onChange={handleChange}
                    placeholder="Hauptplatz 12"
                    data-testid="register-street-input"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-1">
                    <Label htmlFor="address_postal_code">PLZ</Label>
                    <Input
                      id="address_postal_code"
                      name="address_postal_code"
                      type="text"
                      required
                      autoComplete="postal-code"
                      value={form.address_postal_code}
                      onChange={handleChange}
                      placeholder="1010"
                      data-testid="register-postal-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="address_city">Ort</Label>
                    <Input
                      id="address_city"
                      name="address_city"
                      type="text"
                      required
                      autoComplete="address-level2"
                      value={form.address_city}
                      onChange={handleChange}
                      placeholder="Wien"
                      data-testid="register-city-input"
                    />
                  </div>
                </div>

                {/* Abweichende Rechnungs-E-Mail-Adresse — direkt im selben Block */}
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() =>
                      set(
                        "billing_address_different",
                        !form.billing_address_different
                      )
                    }
                    data-testid="register-toggle-billing"
                    aria-pressed={form.billing_address_different}
                    className={[
                      "w-full flex items-center justify-between gap-3 px-4 py-3 border rounded-sm transition-crisp focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400",
                      form.billing_address_different
                        ? "border-brand-300 bg-brand-50"
                        : "border-ink-200 bg-white hover:border-ink-400",
                    ].join(" ")}
                  >
                    <div className="text-left">
                      <div className="text-sm font-medium text-ink-900">
                        Abweichende Rechnungs-E-Mail-Adresse
                      </div>
                    </div>
                    <div
                      className={[
                        "shrink-0 flex h-7 w-7 items-center justify-center rounded-sm border transition-crisp",
                        form.billing_address_different
                          ? "border-brand-500 bg-brand-400 text-ink-900"
                          : "border-ink-300 bg-white text-warm-600",
                      ].join(" ")}
                    >
                      {form.billing_address_different ? (
                        <Minus size={14} weight="bold" />
                      ) : (
                        <Plus size={14} weight="bold" />
                      )}
                    </div>
                  </button>

                  {form.billing_address_different && (
                    <div
                      className="mt-4"
                      data-testid="register-billing-fields"
                    >
                      <Label htmlFor="billing_email">
                        Rechnungs-E-Mail-Adresse
                      </Label>
                      <Input
                        id="billing_email"
                        name="billing_email"
                        type="email"
                        required={form.billing_address_different}
                        autoComplete="email"
                        value={form.billing_email}
                        onChange={handleChange}
                        placeholder="rechnung@firma.at"
                        data-testid="register-billing-email-input"
                      />
                      <p className="mt-1.5 text-[11px] text-warm-500">
                        Rechnungen werden an diese Adresse gesendet — die
                        Login-E-Mail bleibt unverändert.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </section>
          </>
        )}

        {/* ERROR */}
        {error && (
          <div
            data-testid="register-error"
            role="alert"
            className="flex items-start gap-2 border border-status-abgelehnt-border bg-status-abgelehnt-bg px-3 py-2 rounded-sm text-sm text-status-abgelehnt-text"
          >
            <Warning size={16} weight="fill" className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {sizeSelected && (
          <>
            {/* CONSENT CHECKBOXES — placeholders, links to be filled later */}
            <div className="space-y-3 border-t border-ink-200 pt-6">
              <CheckboxRow
                id="consent-dsgvo"
                checked={agreeDsgvo}
                onChange={setAgreeDsgvo}
                testId="register-consent-dsgvo"
              >
                Ich akzeptiere die{" "}
                <a
                  href="#"
                  className="font-medium text-ink-900 underline underline-offset-4 hover:no-underline"
                  onClick={(e) => e.preventDefault()}
                >
                  Datenschutzbestimmungen
                </a>
                .
              </CheckboxRow>
              <CheckboxRow
                id="consent-agb"
                checked={agreeAgb}
                onChange={setAgreeAgb}
                testId="register-consent-agb"
              >
                Ich akzeptiere die{" "}
                <a
                  href="#"
                  className="font-medium text-ink-900 underline underline-offset-4 hover:no-underline"
                  onClick={(e) => e.preventDefault()}
                >
                  Allgemeinen Geschäftsbedingungen
                </a>
                .
              </CheckboxRow>
            </div>

            <Button
              type="submit"
              variant="brand"
              size="lg"
              className="w-full uppercase tracking-wider"
              disabled={!canSubmit}
              data-testid="register-submit-btn"
            >
              {submitting
                ? "Wird gesendet…"
                : "Jetzt zahlungspflichtig bestellen"}
            </Button>
          </>
        )}
      </form>
    </AuthLayout>
  );
}
